import type { LogEntry } from "../../shared/api.js";
import { Page } from "../Page.js";
import { h } from "../dom.js";
import { time } from "../format.js";

/** Craft events, failures and Butler messages, newest first. */
export class LogPage extends Page {
  readonly title = "Log";
  private readonly list = h("ol", { class: "log" });
  private readonly level = h("select", null, h("option", { value: "debug" }, "Everything"), h("option", { value: "info", selected: true }, "Info and up"), h("option", { value: "warn" }, "Warnings and errors"), h("option", { value: "error" }, "Errors only"));
  private oldest: number | undefined;
  private readonly more = h("button", { onclick: () => void this.load(false) }, "Load older");

  mount(): void {
    this.level.addEventListener("change", () => void this.load(true));
    this.el.append(h("div", { class: "toolbar" }, h("h2", null, "Log"), h("span", { class: "spacer" }), this.level), this.list, this.more);
    this.listen("log", () => {
      const entry = this.ctx.live.lastLog;
      if (entry && this.accepts(entry)) this.list.prepend(this.item(entry));
    });
    // Without the WebSocket, new entries arrive only by reloading.
    this.listen("snapshot", () => {
      if (this.ctx.live.polling && this.oldest !== undefined) void this.refreshNewest();
    });
    void this.load(true);
  }

  private newestId(): number {
    return Number((this.list.firstElementChild as HTMLElement | null)?.dataset.id ?? 0);
  }

  private async refreshNewest(): Promise<void> {
    const entries = await this.ctx.api.logs(undefined, this.level.value, 50).catch(() => []);
    const newest = this.newestId();
    for (const e of entries.filter((e) => e.id > newest).reverse()) this.list.prepend(this.item(e));
  }

  private accepts(entry: LogEntry): boolean {
    const order = ["debug", "info", "warn", "error"];
    return order.indexOf(entry.level) >= order.indexOf(this.level.value);
  }

  private item(e: LogEntry): HTMLElement {
    return h("li", { class: `log-${e.level}`, "data-id": String(e.id) }, h("time", null, time(e.at)), h("span", { class: "log-source" }, e.source), h("span", { class: "log-msg" }, e.msg));
  }

  private async load(reset: boolean): Promise<void> {
    if (reset) {
      this.oldest = undefined;
      this.list.replaceChildren();
    }
    const entries = await this.attempt(() => this.ctx.api.logs(this.oldest, this.level.value));
    if (!entries) return;
    for (const e of entries) this.list.appendChild(this.item(e));
    if (entries.length) this.oldest = entries[entries.length - 1].id;
    this.more.hidden = entries.length < 200;
  }
}
